import { useSelector } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';

import { Box, Menu, IconButton, Typography, Button, Badge } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';

import { CircleLoading } from 'shared';
import { notificationsStyles } from './styles/style';
import { useNotificationsService } from 'store/services/notifications.service';

const NotificationsDropdown = () => {
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, hasNext: false });

    const classes = notificationsStyles({});
    const companyId = useSelector((state) => state?.company?.data?.id);
    const { getNotifications, markNotificationAsRead } = useNotificationsService();

    const fetchNotifications = useCallback(
        async (page = 1) => {
            if (!companyId) return;

            try {
                setLoading(true);
                const response = await getNotifications({ companyId, page, pageSize: 10 });

                if (response?.results) {
                    setNotifications((prev) => {
                        const newNotifications =
                            page === 1 ? response.results : [...prev, ...response.results];

                        const unread = newNotifications.filter((n) => !n.is_read).length;
                        setUnreadCount(unread);

                        return newNotifications;
                    });

                    setPagination({ page: response.page, hasNext: response.has_next });
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
            } finally {
                setLoading(false);
            }
        },
        [companyId, getNotifications],
    );

    useEffect(() => {
        if (companyId) {
            fetchNotifications(1);
        }
    }, [companyId]);

    const handleLoadMore = () => {
        if (pagination.hasNext && !loading) {
            fetchNotifications(pagination.page + 1);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (notification) => {
        if (notification.is_read) return;

        try {
            await markNotificationAsRead({ companyId, notificationId: notification.id });
            setNotifications((prev) =>
                prev.map((n) =>
                    n.id === notification.id
                        ? { ...n, is_read: true, read_at: new Date().toISOString() }
                        : n,
                ),
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        const unread = notifications.filter((n) => !n.is_read);
        if (!unread.length) return;

        try {
            for (const n of unread) {
                await markNotificationAsRead({ companyId, notificationId: n.id });
            }

            setNotifications((prev) =>
                prev.map((n) =>
                    n.is_read
                        ? n
                        : { ...n, is_read: true, read_at: n.read_at || new Date().toISOString() },
                ),
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
        };

        for (const [unit, secondsInUnit] of Object.entries(intervals)) {
            const interval = Math.floor(seconds / secondsInUnit);
            if (interval >= 1) {
                return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
            }
        }

        return 'Just now';
    };

    return (
        <>
            <IconButton onClick={handleClick} color="inherit" sx={classes.iconButton}>
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    overlap="circular"
                    invisible={unreadCount === 0}
                    sx={classes.badge}
                >
                    <NotificationsOutlinedIcon fontSize="1.5rem" />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: classes.menuPaper }}
            >
                <Box p={2.5} sx={classes.menuBox}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={700}>
                            Notifications {unreadCount > 0 && `(${unreadCount} new)`}
                        </Typography>
                        {unreadCount > 0 && (
                            <Button
                                size="small"
                                color="primary"
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                onClick={handleMarkAllAsRead}
                                disabled={loading}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Box>

                    <Box sx={classes.notificationsList}>
                        {loading && pagination.page === 1 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight={100}
                            >
                                <CircleLoading size={24} />
                            </Box>
                        ) : notifications.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight={100}
                            >
                                <Typography color="textSecondary">No notifications</Typography>
                            </Box>
                        ) : (
                            <>
                                {notifications.map((notification) => (
                                    <Box
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={classes.notificationItem}
                                    >
                                        <Box flexGrow={1}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={
                                                    notification.is_read ? 'normal' : 'bold'
                                                }
                                                sx={{ mb: 0.5 }}
                                            >
                                                {notification.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={classes.notificationDesc}
                                            >
                                                {notification.message}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {formatTimeAgo(notification.created_at)}
                                            </Typography>
                                        </Box>
                                        {!notification.is_read && <Box sx={classes.unreadDot} />}
                                    </Box>
                                ))}
                                {pagination.hasNext && (
                                    <Box textAlign="center" mt={1}>
                                        <Button
                                            size="small"
                                            onClick={handleLoadMore}
                                            disabled={loading}
                                        >
                                            {loading ? 'Loading...' : 'Load More'}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Menu>
        </>
    );
};

export default NotificationsDropdown;
